import { useState } from "react";
import {
  Body1,
  Button,
  Caption1,
  Card,
  CardFooter,
  CardHeader,
  CardPreview,
  Text,
} from "@fluentui/react-components";
import type { MingleEvent } from "../api/events";
import { getAvailability } from "../api/signup";
import SignupDialog from "./SignupDialog";
import styles from "./EventCard.module.css";
import Logo from "../Logo";
import { format } from "date-fns";

type Props = {
  event: MingleEvent;
};

/** Searches for the restaurant by name as well as address, so the pin lands
 * on the venue rather than the middle of the street. */
function mapsUrl({ restaurantName, address }: MingleEvent) {
  const query = [restaurantName, address].filter(Boolean).join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query
  )}`;
}

export default function EventCard({ event }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const day = event.date ? format(event.date, "EEEE, MMMM d") : "";
  const time = event.date ? format(event.date, "h:mm a") : "";

  const availability = getAvailability(event);
  const isFull = availability.kind === "full";

  const badge = {
    open: { label: "Open", className: styles.badgeOpen },
    limited: {
      label:
        availability.kind === "limited"
          ? `${availability.seatsLeft} ${
              availability.seatsLeft === 1 ? "place" : "places"
            } left`
          : "",
      className: styles.badgeLimited,
    },
    full: { label: "Full", className: styles.badgeFull },
  }[availability.kind];

  return (
    <Card className={styles.card}>
      {event.image ? (
        <CardPreview className={styles.preview} logo={<Logo size={3} />}>
          <img
            className={styles.img}
            src={event.image}
            alt={event.restaurantName}
          />
        </CardPreview>
      ) : null}

      <CardHeader
        header={
          <Text size={500} weight="semibold">
            {event.restaurantName}
          </Text>
        }
        action={
          <span className={`${styles.badge} ${badge.className}`}>
            {badge.label}
          </span>
        }
      />

      <div className={styles.details}>
        <div className={styles.when}>
          <span className={styles.day}>{day}</span>
          <span className={styles.time}>{time}</span>
        </div>

        {event.address ? (
          <Caption1 className={styles.meta}>
            {event.neighborhood ? `${event.neighborhood} · ` : ""}
            <a
              className={styles.mapLink}
              href={mapsUrl(event)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {event.address}
            </a>
          </Caption1>
        ) : null}

        {event.description ? (
          <Body1 className={styles.description}>{event.description}</Body1>
        ) : null}

        {event.charity ? (
          <Caption1 className={styles.meta}>
            Proceeds support {event.charity}
          </Caption1>
        ) : null}
      </div>

      <CardFooter>
        <Button
          appearance={isFull ? "outline" : "primary"}
          className={styles.cta}
          onClick={() => setDialogOpen(true)}
        >
          {isFull ? "Join waitlist" : "Sign up"}
        </Button>
      </CardFooter>

      <SignupDialog
        event={event}
        isFull={isFull}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </Card>
  );
}
